/**
 * LA PESTAÑA DE MACROS DEL CLIENTE: quién ve qué, y el editor.
 *
 * Tres caminos, y los tres acaban en una pantalla del documento de Jesús del 7-09:
 *
 *   no puede tocarlos      «Mis macros» en solo lectura (pantalla 01)
 *   puede y no está edit.  «Mis macros» con «Cambiar mis macros» (pantalla 02)
 *   está editando          este editor (pantalla 04) y su confirmación (pantalla 05)
 *
 * EL EDITOR SE VACIÓ A PROPÓSITO (decisión de Francisco, 7-09, sobre la regla 04·«por qué»).
 * Encima de los tres bloques vivía la calculadora entera -- peso, % graso, sexo, objetivo y
 * las cinco preguntas del motor --, y en el móvil esta pantalla medía 6.858 píxeles: el
 * cliente pasaba por medio cuestionario para subirse 15 g de hidratos. Su regla: «los tres
 * bloques a la vez, como los tienes en tu calculadora... y nada de explicaciones debajo. Se ve
 * lo que hay que hacer».
 *
 * DÓNDE FUE A PARAR LO QUE SE QUITÓ: calcular con el motor sigue viviendo en «Revisar mis
 * macros» (el cuestionario de ajuste, /questionnaire?ajustar=1), que es lo que su plan le abre
 * cuando toca y donde esas preguntas tienen sentido. Aquí no se calcula: aquí se cambia.
 *
 * Y POR ESO EL GUARDADO MANDA `ajustes: null`: sin preguntas en pantalla no hay respuestas
 * nuevas que dar, y el servidor solo escribe `ajustes_macros` cuando le llega algo (ver
 * `PUT /macros` en routes/users.py). Mandar el objeto vacío le habría borrado las suyas.
 */
import React, { useState, useEffect } from 'react';
import { SlidersHorizontal, Loader2, Save, CalendarDays, Minus, Plus, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { MACRO } from './ClientDashboard';
import MisMacrosPage, { rotuloDelPeri, useOpcionPeri } from './MisMacrosPage';
import { mensajeDeError } from '../lib/mensajeDeError';
import { anotar } from '../lib/rastro';
import { num1 } from '../lib/numeros';
import { hoyEnEspana, diaEnEspana } from '../lib/horaEspana';

// EL «MAÑANA» DE LA FECHA DE APLICACIÓN VA EN DÍA DE ESPAÑA, no en el reloj del cliente.
//
// Y es la trampa de siempre de esta app, medida aquí el 7-09 a las 19:03 de Argentina: en
// Madrid ya eran las 00:03 del día 8. Con el reloj del cliente, «mañana» era el 8 -- que para
// el servidor YA ERA HOY --, así que el cambio que él programaba para el día siguiente entraba
// en vigor al instante. Justo lo contrario de lo que pide la regla 04·5, que existe para que
// «cambiar los macros a mitad de tarde no le descuadre la dieta que ya tenía creada para hoy».
//
// La vigencia la resuelve el servidor en día de España (`hasta_hoy`, `hoy_madrid`), así que
// aquí se pregunta con el mismo reloj: las dos puntas tienen que contar los días igual. Es la
// misma regla que ya se aplicó al arranque del ciclo.

const hoyISO = () => hoyEnEspana();
const mananaISO = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return diaEnEspana(d);
};

const fechaLarga = (iso) => {
    if (!iso) return '';
    return new Date(iso + 'T12:00:00').toLocaleDateString('es-ES', {
        weekday: 'long', day: 'numeric', month: 'long',
    });
};

// CÓMO SE MUEVE UN MACRO: con − y +, como lo dibujó, y tecleando si quiere el número exacto.
//
// El paso es de 5 g porque es como se mueven los macros en su calculadora y en sus ajustes;
// quien necesite un 132 lo escribe. Sin rangos ni topes (regla 04·1: «se pone el número que
// quiera. Es su plan»), solo el suelo del cero, que no es una opinión.
const PASO = 5;

// El nombre entero de cada casilla: «Hidratos del día de entrenamiento». En la pantalla se lee
// solo «Hidratos» -- el bloque ya dice de qué día es --, pero el lector de pantalla y el foco
// necesitan el nombre completo, porque hay tres «Hidratos» en la misma página.
const Campo = ({ id, label, deQue, color, valor, onChange }) => {
    const n = parseFloat(valor);
    const mueve = (delta) => onChange(String(Math.max(0, (Number.isNaN(n) ? 0 : n) + delta)));
    const entero = `${label} · ${deQue}`;
    return (
        <div className="flex items-center justify-between gap-3 py-2">
            {/* Con `htmlFor`, que si no el label no etiqueta nada: pulsar el nombre no llevaba
                el foco a la casilla y el lector de pantalla anunciaba un número sin nombre. */}
            <label htmlFor={id} className="text-sm text-muted-foreground" style={{ color }}>{label}</label>
            <div className="flex items-center gap-1.5">
                <button type="button" onClick={() => mueve(-PASO)} aria-label={`Bajar ${entero}`}
                    className="w-9 h-9 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:border-foreground/40 flex items-center justify-center transition-colors">
                    <Minus className="w-4 h-4" />
                </button>
                <input id={id} type="number" min="0" step="1" value={valor}
                    onChange={e => onChange(e.target.value)} aria-label={entero}
                    className="w-16 text-center bg-transparent border-0 font-data text-lg font-bold text-foreground focus:outline-none focus:ring-1 focus:ring-brand rounded" />
                <button type="button" onClick={() => mueve(PASO)} aria-label={`Subir ${entero}`}
                    className="w-9 h-9 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:border-foreground/40 flex items-center justify-center transition-colors">
                    <Plus className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};

const Bloque = ({ label, grupo, conGrasa, macros, setMacro }) => (
    <div className="surface p-4">
        <p className="caption mb-1">{label}</p>
        <Campo id={`${grupo}-protein`} label="Proteína" deQue={label} color={MACRO.protein}
            valor={macros[grupo].protein} onChange={v => setMacro(grupo, 'protein', v)} />
        <Campo id={`${grupo}-carbs`} label="Hidratos" deQue={label} color={MACRO.carbs}
            valor={macros[grupo].carbs} onChange={v => setMacro(grupo, 'carbs', v)} />
        {/* El perientreno solo lleva proteínas e hidratos, sin grasas, como en su calculadora
            (regla 04·2). */}
        {conGrasa && (
            <Campo id={`${grupo}-fat`} label="Grasa" deQue={label} color={MACRO.fat}
                valor={macros[grupo].fat} onChange={v => setMacro(grupo, 'fat', v)} />
        )}
    </div>
);

const BloquePeri = (props) => <Bloque label={rotuloDelPeri(useOpcionPeri())} {...props} />;

const VACIO = { protein: '', carbs: '', fat: '' };
const num = (v) => { const n = parseFloat(v); return Number.isNaN(n) ? null : n; };

// LO QUE CAMBIA, Y SOLO LO QUE CAMBIA (regla 05·1: «si solo movió los hidratos del día de
// entreno, solo salen esos tres. No se le repite lo que ha dejado igual»).
const LINEAS = [
    ['training', 'Entreno', ['protein', 'carbs', 'fat']],
    ['peri', 'Perientreno', ['protein', 'carbs']],
    ['rest', 'Descanso', ['protein', 'carbs', 'fat']],
];
const NOMBRE = { protein: 'P', carbs: 'H', fat: 'G' };

const loQueCambia = (antes, ahora) => {
    const filas = [];
    for (const [grupo, titulo, campos] of LINEAS) {
        for (const c of campos) {
            const a = num(antes?.[grupo]?.[c]);
            const b = num(ahora?.[grupo]?.[c]);
            if (b == null) continue;
            if (a == null || a !== b) filas.push({ etiqueta: `${titulo} · ${NOMBRE[c]}`, antes: a, ahora: b });
        }
    }
    return filas;
};

const MacroCalculatorClientPage = () => {
    const { api, profile } = useAuth();

    const [macros, setMacros] = useState({ training: { ...VACIO }, rest: { ...VACIO }, peri: { ...VACIO } });
    // Los que tenía antes de tocar nada: para la confirmación y para «Volver a los anteriores».
    const [previos, setPrevios] = useState(null);
    // A PARTIR DE MAÑANA POR DEFECTO (regla 04·5). Era hoy, y eso es justo lo que él quería
    // evitar: «cambiar los macros a mitad de tarde le descuadra la dieta que ya tenía creada
    // para hoy». Puede elegir otro día, y después moverlo desde el bloque del cambio programado.
    const [desde, setDesde] = useState(mananaISO());
    const [peso, setPeso] = useState('');
    const [ajustando, setAjustando] = useState(false);
    const [confirmando, setConfirmando] = useState(false);
    const [cargando, setCargando] = useState(true);
    const [guardando, setGuardando] = useState(false);

    useEffect(() => {
        let vivo = true;
        api.get('/macros').then(res => {
            if (!vivo || !res.data) return;
            const d = res.data;
            const pick = (m, a, b) => (m?.[a] ?? m?.[b] ?? '');
            const suyos = {
                training: { protein: pick(d.training, 'protein', 'proteinas'), carbs: pick(d.training, 'carbs', 'hidratos'), fat: pick(d.training, 'fat', 'grasas') },
                rest: { protein: pick(d.rest, 'protein', 'proteinas'), carbs: pick(d.rest, 'carbs', 'hidratos'), fat: pick(d.rest, 'fat', 'grasas') },
                peri: { protein: pick(d.periworkout, 'protein', 'proteinas'), carbs: pick(d.periworkout, 'carbs', 'hidratos'), fat: '' },
            };
            setMacros(suyos);
            setPrevios(suyos);
            if (d.peso != null) setPeso(String(d.peso));
        }).catch(() => {}).finally(() => { if (vivo) setCargando(false); });
        return () => { vivo = false; };
    }, [api]);

    const setMacro = (grupo, campo, valor) =>
        setMacros(prev => ({ ...prev, [grupo]: { ...prev[grupo], [campo]: valor } }));

    // ABRIR EL EDITOR, y si viene por «Volver a los anteriores», con los números del ajuste
    // de antes ya puestos. Se piden al historial (`entradas[1]`, el anterior al vigente); si
    // falla o no hay, se abre con los de ahora y no se le dice nada raro: puede moverlos igual.
    // Carga en el formulario los del ajuste anterior al vigente. Devuelve si los había.
    // El primero con OTROS números: el historial trae ajustes repetidos (los migrados y los
    // de «asociar al peso» sin cambiar nada), y cargar uno igual parecía un botón muerto.
    const cargarAnteriores = async () => {
        try {
            const { data } = await api.get('/macros/historial');
            const entradas = data?.entradas || [];
            const huella = (e) => JSON.stringify([e?.entreno, e?.descanso, e?.peri].map((b) => [b?.proteina, b?.hidratos, b?.grasa]));
            const previo = entradas.slice(1).find((e) => huella(e) !== huella(entradas[0]));
            if (!previo) return false;
            const bloque = (b, conGrasa = true) => ({
                protein: b?.proteina ?? '', carbs: b?.hidratos ?? '',
                fat: conGrasa ? (b?.grasa ?? '') : '',
            });
            setMacros({
                training: bloque(previo.entreno),
                rest: bloque(previo.descanso),
                peri: bloque(previo.peri, false),
            });
            return true;
        } catch (e) {
            console.error('No se pudieron traer los macros anteriores', e);
            return false;
        }
    };

    const abrirEditor = async (conLosAnteriores = false) => {
        if (conLosAnteriores) await cargarAnteriores();
        setAjustando(true);
    };

    // EL BOTÓN DE DENTRO DEL EDITOR hace lo mismo que el de la portada (Francisco, 23-09: «el
    // botón de volver a los anteriores no funciona»). Devolvía los números con los que se
    // abrió el editor: sin haber tecleado nada ponía los mismos y parecía muerto. Ahora trae
    // los del ajuste anterior; si no hay, lo dice.
    const volverALosAnteriores = async () => {
        if (await cargarAnteriores()) {
            toast.success('Te he puesto los macros que tenías antes. Guarda para volver a ellos.');
        } else {
            toast.info('No tienes macros anteriores: estos son los primeros que tienes.');
        }
    };

    const cambios = loQueCambia(previos, macros);
    // Pulsó Guardar sin cambiar nada: se queda escrito bajo el botón, no en un aviso que se va
    // (Jesús Sánchez, 27-09: «no me dejaba guardar los cambios» con los números que ya tenía).
    const [sinCambios, setSinCambios] = useState(false);
    useEffect(() => { if (cambios.length) setSinCambios(false); }, [cambios.length]);

    const guardar = async () => {
        const t = macros.training, r = macros.rest, p = macros.peri;
        if ([t.protein, t.carbs, t.fat, r.protein, r.carbs, r.fat].some(v => num(v) == null)) {
            toast.error('Completa proteínas, hidratos y grasas de los días de entreno y descanso');
            return;
        }
        setGuardando(true);
        try {
            await api.put('/macros', {
                training: { protein: num(t.protein), carbs: num(t.carbs), fat: num(t.fat) },
                rest: { protein: num(r.protein), carbs: num(r.carbs), fat: num(r.fat) },
                peri: { protein: num(p.protein) || 0, carbs: num(p.carbs) || 0 },
                effective_date: desde,
                // «GUARDAR ASOCIADO A PESO» (regla 04·4): «es de donde sale la columna del peso
                // del histórico. Sin eso, el ajuste no se puede leer después». Y se nota en los
                // datos: 359 filas de producción tienen el peso vacío.
                peso: num(peso),
                // Sin preguntas en pantalla no hay respuestas nuevas: null para que el servidor
                // no toque las que ya tiene guardadas.
                ajustes: null,
            });
            // Y SE DICE QUE SE LE MUEVEN LAS CANTIDADES. El servidor recuadra los días que ya
            // tuviera montados (regla 04·7), y eso es algo que le pasa a su comida: callarlo
            // sería que abriera Nutrición y encontrara otros gramos sin saber por qué.
            //
            // Sin número, porque el recuadre va detrás de la respuesta: cuando esto se pinta,
            // el servidor todavía está en ello. Prometer «tres días» y equivocarse es peor que
            // decir lo que va a pasar.
            const cuando = desde === hoyISO() ? 'Ya son tus macros de hoy.' : `Empiezan el ${fechaLarga(desde)}.`;
            toast.success(`Guardado. ${cuando} Si ya tenías esos días creados, ajustamos las cantidades.`);
            // De vuelta a la portada, que es donde ve el resultado: si lo guardó para más
            // adelante, ahí le sale el bloque del cambio programado. Y los recién guardados
            // pasan a ser «los anteriores» de la próxima vez que entre a cambiarlos.
            setPrevios(macros);
            setConfirmando(false);
            setAjustando(false);
        } catch (err) {
            toast.error(mensajeDeError(err, 'No hemos podido guardar tus macros'));
        } finally {
            setGuardando(false);
        }
    };

    // ¿PUEDE ESTE CLIENTE AJUSTARSE LOS MACROS? Lo decide el servidor a partir del campo
    // `calculadora` de su plan (punto 4.10); aquí solo se obedece.
    const ajustables = profile?.macros_ajustables;
    const puedeAjustar = ajustables ? ajustables.puede !== false : true;

    if (!puedeAjustar) return <MisMacrosPage />;
    if (!ajustando && profile?.macros_training) {
        // La portada abre el editor de dos maneras: a secas («Cambiar mis macros») o con los
        // números del ajuste ANTERIOR ya cargados («Volver a los anteriores», regla 07·5).
        // Lo segundo no deshace nada por detrás: le deja el camino de vuelta hecho y guarda
        // como un ajuste suyo más, para que el histórico siga contando lo que de verdad pasó.
        return <MisMacrosPage onAjustar={(modo) => abrirEditor(modo === 'anteriores')} />;
    }

    // ── PANTALLA 05 · LA CONFIRMACIÓN ──
    if (confirmando) {
        return (
            <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-[560px] mx-auto space-y-5 animate-fade-in">
                <h1 className="font-heading text-2xl md:text-3xl font-bold uppercase text-foreground leading-none"
                    data-testid="confirmar-heading">
                    {/* El nombre es el del vídeo del 7-09 (4:48), donde lo ensaya seis veces
                        hasta dejarlo: sustituye al «¿Guardamos estos macros?» del documento. */}
                    Confirmamos entonces estos cambios
                </h1>

                <section className="surface p-5 space-y-3" data-testid="confirmar-cambios">
                    <p className="text-sm text-muted-foreground">Esto es lo que cambia.</p>
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                                <th className="text-left font-semibold py-1.5"> </th>
                                <th className="text-right font-semibold py-1.5">Antes</th>
                                <th className="text-right font-semibold py-1.5">Ahora</th>
                                <th className="text-right font-semibold py-1.5">Dif</th>
                            </tr>
                        </thead>
                        <tbody>
                            {cambios.map(f => {
                                const dif = f.antes == null ? null : f.ahora - f.antes;
                                return (
                                    <tr key={f.etiqueta} className="border-b border-border last:border-0">
                                        <td className="py-2 text-muted-foreground">{f.etiqueta}</td>
                                        <td className="py-2 text-right font-data text-muted-foreground">
                                            {f.antes == null ? '-' : num1(f.antes)}
                                        </td>
                                        <td className="py-2 text-right font-data font-bold text-foreground">{num1(f.ahora)}</td>
                                        <td className={`py-2 text-right font-data font-bold ${
                                            dif ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'}`}>
                                            {dif == null ? '-' : dif > 0 ? `+${num1(dif)}` : num1(dif)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    {/* LAS DOS COSAS QUE NO HA DECIDIDO ÉL (regla 05·2): a qué peso se guardan y
                        desde cuándo valen. Con la puerta abierta, que es lo que él marcó como
                        importante en el vídeo y repitió dos veces. */}
                    <p className="text-sm text-muted-foreground border-t border-border pt-3">
                        {num(peso) != null && <>Se guardan asociados a <span className="font-semibold text-foreground">{num1(num(peso))} kg</span> y s</>}
                        {num(peso) == null && 'S'}e aplican{' '}
                        <span className="font-semibold text-foreground">
                            {desde === mananaISO() ? 'a partir de mañana' : `desde el ${fechaLarga(desde)}`}
                        </span>. Después podrás modificar la fecha de aplicación si quieres.
                    </p>
                </section>

                {/* Sí y volver atrás. Sin tercera opción (regla 05·3). */}
                <div className="flex gap-2 flex-wrap">
                    <button type="button" onClick={guardar} disabled={guardando} data-testid="si-confirmamos"
                        className="flex-1 min-w-[150px] py-3 rounded-xl bg-brand text-white font-bold hover:opacity-90 disabled:opacity-60 inline-flex items-center justify-center gap-2">
                        {guardando ? <><Loader2 className="w-4 h-4 animate-spin" /> Guardando...</> : 'Sí, confirmamos'}
                    </button>
                    <button type="button" onClick={() => setConfirmando(false)} disabled={guardando}
                        data-testid="volver-atras"
                        className="flex-1 min-w-[150px] py-3 rounded-xl border border-border text-foreground font-semibold hover:border-foreground/40">
                        Volver atrás
                    </button>
                </div>
            </div>
        );
    }

    // ── PANTALLA 04 · CAMBIAR MIS MACROS ──
    return (
        <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-[560px] mx-auto space-y-4 animate-fade-in">
            <button type="button" onClick={() => setAjustando(false)}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                data-testid="volver-a-mis-macros">
                <ArrowLeft className="w-4 h-4" /> Mis macros
            </button>

            <header className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-brand/10">
                    <SlidersHorizontal className="w-5 h-5 text-brand" />
                </div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold uppercase text-foreground leading-none"
                    data-testid="macros-heading">
                    Cambiar mis macros
                </h1>
            </header>

            {cargando ? (
                <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-muted-foreground" /></div>
            ) : (<>
                <Bloque label="Día de entrenamiento" grupo="training" conGrasa macros={macros} setMacro={setMacro} />
                <BloquePeri grupo="peri" conGrasa={false} macros={macros} setMacro={setMacro} />
                <Bloque label="Día de descanso" grupo="rest" conGrasa macros={macros} setMacro={setMacro} />

                <div className="surface p-4 space-y-4">
                    <div className="flex items-center justify-between gap-3">
                        <label htmlFor="peso-del-ajuste" className="text-sm font-semibold text-foreground">
                            Guardar asociado a peso
                        </label>
                        <div className="flex items-center gap-1.5">
                            <input id="peso-del-ajuste" type="number" min="0" step="0.1" value={peso}
                                onChange={e => setPeso(e.target.value)} placeholder="-"
                                data-testid="peso-del-ajuste"
                                className="w-20 text-right bg-transparent border-0 font-data text-lg font-bold text-foreground focus:outline-none focus:ring-1 focus:ring-brand rounded" />
                            <span className="text-sm text-muted-foreground">kg</span>
                        </div>
                    </div>

                    <div className="border-t border-border pt-3">
                        <label htmlFor="desde-cuando" className="flex items-center gap-2 text-sm font-semibold text-foreground mb-2">
                            <CalendarDays className="w-4 h-4 text-brand" /> A partir de
                        </label>
                        <input id="desde-cuando" type="date" value={desde} min={hoyISO()}
                            onChange={e => setDesde(e.target.value)} data-testid="desde-cuando"
                            className="input-light font-data" />
                        <p className="text-xs text-muted-foreground mt-2">
                            {desde === hoyISO()
                                ? 'Se aplican hoy: la dieta que ya tengas creada para hoy se queda con los números viejos.'
                                : `Empiezas con estos el ${fechaLarga(desde)}; hasta entonces sigues con los de ahora.`}
                        </p>
                    </div>
                </div>

                <div className="flex gap-2 flex-wrap">
                    <button type="button" data-testid="guardar-macros"
                        onClick={() => {
                            if (cambios.length) { setConfirmando(true); return; }
                            setSinCambios(true);
                            anotar({ tipo: 'freno', mensaje: 'Guardar macros sin cambios' });
                        }}
                        className="flex-1 min-w-[150px] py-3 rounded-xl bg-brand text-white font-bold hover:opacity-90 inline-flex items-center justify-center gap-2">
                        <Save className="w-4 h-4" /> Guardar
                    </button>
                    {/* «VOLVER A LOS ANTERIORES», al lado de Guardar (regla 04·6). Trae al formulario
                        los macros del ajuste anterior al vigente; no deshace nada por detrás:
                        para volver de verdad hay que guardar, y queda como un ajuste suyo más. */}
                    <button type="button" onClick={volverALosAnteriores}
                        data-testid="volver-a-los-anteriores"
                        className="flex-1 min-w-[150px] py-3 rounded-xl border border-border text-foreground font-semibold hover:border-foreground/40">
                        Volver a los anteriores
                    </button>
                </div>
                {sinCambios && (
                    <p data-testid="sin-cambios" className="text-sm text-muted-foreground mt-2">
                        Estos números ya son tus macros de ahora, así que no hay nada que guardar.
                        Cambia alguno y vuelve a pulsar Guardar.
                    </p>
                )}
            </>)}
        </div>
    );
};

export default MacroCalculatorClientPage;
